import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormFeedback, Input } from 'reactstrap';
import { ErrorMessage } from '@hookform/error-message';
import { generateRandomString } from 'src/helpers/utils';

interface CustomInputFieldProps {
  children?: React.ReactNode;
  label?: string;
  name: string;
  placeholder?: string;
  type?: string;
  clearError?: boolean;
  highlightBorderOnError?: boolean;
  customOnChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  customOnChangeHandler?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  errorMessageProps?: React.ComponentProps<typeof FormFeedback>;
  helperText?: string;
  hideFormError?: boolean;
  [key: string]: unknown;
}

const CustomInputField = (props: CustomInputFieldProps) => {
  const {
    control,
    formState: { errors },
    clearErrors,
  } = useFormContext();

  const {
    children,
    label,
    name: propsName,
    placeholder,
    type,
    clearError,
    highlightBorderOnError,
    errorMessageProps,
    customOnChange,
    customOnChangeHandler,
    helperText,
    hideFormError,
    ...inputProps
  } = props;

  return (
    <React.Fragment>
      <Controller
        control={control}
        name={propsName}
        render={({ field }) => (
          <Input
            autoComplete="off"
            name={field?.name}
            onBlur={field?.onBlur}
            onChange={(event) => {
              if (customOnChange) {
                customOnChange?.(event);
              } else if (type === 'checkbox') {
                field?.onChange(event.target.checked);
              } else {
                field?.onChange(event);
              }
              customOnChangeHandler?.(event);
              if (clearError) {
                clearErrors();
              }
            }}
            placeholder={placeholder}
            type={(type || 'text') as 'text' | 'textarea' | 'checkbox'}
            label={label}
            value={type === 'checkbox' ? undefined : (field?.value ?? '')}
            checked={type === 'checkbox' ? !!field?.value : undefined}
            id={generateRandomString()}
            {...(highlightBorderOnError && errors?.[propsName] ? { invalid: true } : {})}
            {...inputProps}
          >
            {children}
          </Input>
        )}
      />
      {helperText ? <small className="form-label-sm">{helperText}</small> : null}

      {!clearError && !hideFormError && (
      <ErrorMessage
        errors={errors}
        name={propsName}
        render={({ message }) => (
          <FormFeedback {...(errorMessageProps || {})}>{message}</FormFeedback>
        )}
      />
      )}

    </React.Fragment>
  );
};

export default CustomInputField;
